import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useUpdateStoreOrderStatusMutation } from "@/modules/stores/services/mutations/useUpdateStoreOrderStatusMutation";
import { useStoreOrderByIdQuery } from "@/modules/stores/services/queries/useStoreOrderByIdQuery";
import { useStoreOrderProofQuery } from "@/modules/stores/services/queries/useStoreOrderProofQuery";
import { BackButton } from "@/shared/layout/BackButton";
import { OrderDetails } from "@/modules/stores/components/orders/OrderDetails";
import { getQueryErrorStatus } from "@/lib/query/core/get-query-error-status";
import { LoadingSkeleton, ErrorState } from "@a2z/ui";

type StoreOrderPageProps = {
  locale: string;
  storeId: string;
  orderId: string;
};

export function StoreOrderPage({
  locale,
  storeId,
  orderId,
}: StoreOrderPageProps) {
  const navigate = useNavigate();
  const t = useTranslations();
  const { mutateAsync: updateOrderStatus } = useUpdateStoreOrderStatusMutation();
  const { data: order, isLoading, error, refetch } = useStoreOrderByIdQuery({
    storeId,
    orderId,
  });
  const {
    data: proofData,
    isLoading: isProofLoading,
    error: proofQueryError,
  } = useStoreOrderProofQuery({
    storeId,
    orderId,
  });
  const orderErrorStatus = getQueryErrorStatus(error);
  const isProofNotFound =
    proofQueryError instanceof ApiJsonClientError && proofQueryError.status === 404;
  const proofItems = isProofNotFound ? [] : (proofData?.items ?? []);
  const proofError = Boolean(proofQueryError) && !isProofNotFound;

  useEffect(() => {
    if (orderErrorStatus === 404) {
      navigate(`/${locale}/admin/stores/${storeId}`, { replace: true });
    }
  }, [locale, orderErrorStatus, navigate, storeId]);

  if (isLoading) {
    return <LoadingSkeleton variant="detail" />;
  }

  if (orderErrorStatus === 404) {
    return null;
  }

  if (error || !order) {
    return <ErrorState onRetry={refetch} />;
  }

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[23px] font-bold leading-tight tracking-[-0.025em] lg:text-[28px] lg:font-semibold lg:tracking-[-0.01em] text-[color:var(--text-primary)]">
            {t(keys.orders.orderDetailsTitle)}
          </h1>
        </div>
        <BackButton
          label={t(keys.common.back)}
          fallbackHref={`/${locale}/admin/stores/${storeId}`}
        />
      </div>

      <OrderDetails
        locale={locale}
        storeId={storeId}
        order={order}
        proofState={{
          items: proofItems,
          isLoading: isProofLoading,
          isError: proofError,
        }}
        onUpdateStatus={(payload) =>
          updateOrderStatus({
            storeId,
            orderId,
            ...payload,
          })
        }
      />
    </section>
  );
}
